import { useCallback, useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import api, { apiUrl } from '../../api'
import { Campaign, CampaignType, Folder, Template } from '../../types'
import Button, { LinkButton } from '../../ui/Button'
import { ArchiveIcon, DuplicateIcon, EditIcon, FolderMoveIcon, PlusIcon } from '../../ui/icons'
import Menu, { MenuItem } from '../../ui/Menu'
import Modal from '../../ui/Modal'
import PageContent from '../../ui/PageContent'
import { Table, useSearchTableQueryState } from '../../components'
import { formatDate, snakeToTitle } from '../../utils'
import { DuplicateCampaignModal, DuplicateCampaignOptions } from './DuplicateCampaignModal'
import { CampaignForm } from './form/CampaignForm'
import PreviewImage from '../../ui/PreviewImage'
import { Alert } from '../../ui'
import { ProjectContext } from '../../contexts'
import { PreferencesContext } from '../../ui/PreferencesContext'
import { useTranslation } from 'react-i18next'
import { FolderSelectModal } from '../../components/table/FolderSelectModal'
import { toast } from 'react-hot-toast/headless'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { TagPicker } from '../settings/TagPicker'
import { campaignChannels, campaignStates, campaignTypes } from './CampaignFields'
import { ChannelIcon } from './ui/ChannelTag'
import { CampaignTag } from './ui/CampaignTag'
import { ClickRate, DeliveryRatio, OpenRate } from './ui/CampaignRates'

export default function Campaigns() {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [preferences] = useContext(PreferencesContext)
    const state = useSearchTableQueryState(
        useCallback(async params => await api.campaigns.search(project.id, params), [project.id]),
        {
            filter: {
                type: ['blast'],
            },
        })
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [campaignToMove, setCampaignToMove] = useState<Campaign | undefined>()
    const [campaignToDuplicate, setCampaignToDuplicate] = useState<{ id: number, type: CampaignType, templates: Template[] } | undefined>()

    const handleCreateCampaign = async (campaign: Campaign) => {
        setIsCreateOpen(false)
        await navigate(`${campaign.id}/design`)
    }

    const handleEditCampaign = async (id: number) => {
        await navigate(id.toString())
    }

    const handleMoveFolder = async (folder: Folder) => {
        if (!campaignToMove) return
        await api.campaigns.update(project.id, campaignToMove.id, { folder_id: folder.id })
        setCampaignToMove(undefined)
        await state.reload()
        toast.success(t('folder_moved_item', { item: campaignToMove.name, folder: folder.name }))
    }

    const duplicateCampaign = async (id: number, { includeVariants, locales, type }: DuplicateCampaignOptions) => {
        const campaign = await api.campaigns.duplicate(project.id, id, includeVariants, locales, type)
        await navigate(campaign.id.toString())
    }

    const handleDuplicateCampaign = async ({ id, type }: Campaign) => {
        const templates = await api.campaigns.templates(project.id, id)
        setCampaignToDuplicate({ id, type, templates })
    }

    const handleArchiveCampaign = async (id: number) => {
        await api.campaigns.delete(project.id, id)
        await state.reload()
    }

    const emptyMessage = Object.keys(state.params.filter ?? {}).some(key => key !== 'folder_id')
        ? t('campaigns_empty_filter', {
            filters: Object.keys(state.params.filter ?? {})
                .filter(k => k !== 'folder_id')
                .map(k => t(k).toLowerCase())
                .join(', '),
        })
        : undefined

    return (
        <>
            <PageContent title={t('campaigns')} actions={
                <Button icon={<PlusIcon />} onClick={() => setIsCreateOpen(true)}>{t('create_campaign')}</Button>
            } banner={project.has_provider === false && (
                <Alert
                    variant="plain"
                    title={t('setup')}
                    actions={
                        <LinkButton to={`/projects/${project.id}/settings/integrations`}>{t('setup_integration')}</LinkButton>
                    }
                >{t('setup_integration_description')}</Alert>
            )}>
                <Table
                    {...state}
                    type="folder"
                    columns={[
                        {
                            key: 'name',
                            title: t('name'),
                            sortable: true,
                            minWidth: '225px',
                            cell: ({ item: { id, name, channel } }) => (
                                <div className="multi-cell">
                                    { channel === 'email'
                                        ? <PreviewImage url={apiUrl(project.id, `campaigns/${id}/preview`)} width={50} height={40}>
                                            <div className="placeholder">
                                                <ChannelIcon channel={channel} />
                                            </div>
                                        </PreviewImage>
                                        : <div className="placeholder">
                                            <ChannelIcon channel={channel} />
                                        </div>
                                    }
                                    <div className="text">
                                        <div className="title">{name}</div>
                                        <div className="subtitle">
                                            {snakeToTitle(channel)}</div>
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: 'state',
                            title: t('state'),
                            sortable: true,
                            cell: ({ item }) => CampaignTag({ campaign: item }),
                        },
                        {
                            key: 'delivery',
                            title: t('delivery'),
                            cell: ({ item: { delivery } }) => (
                                <div className="multi-cell no-image">
                                    <div className="text">
                                        <div className="title">
                                            {DeliveryRatio({ delivery })}
                                        </div>
                                        {(delivery && delivery.total > 0) && <div className="subtitle">
                                            {delivery.total.toLocaleString()} {t('total')}
                                        </div>}
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: 'engagement',
                            title: t('engagement'),
                            cell: ({ item: { channel, delivery } }) => delivery?.opens > 0
                                ? (
                                    <div className="multi-cell no-image">
                                        <div className="text">
                                            <div className="title">
                                                {OpenRate({ delivery })} {t('open_rate')}
                                            </div>
                                            {channel === 'email' && <div className="subtitle">
                                                {ClickRate({ delivery })} {t('click_rate')}
                                            </div>}
                                        </div>
                                    </div>
                                )
                                : null,
                        },
                        {
                            key: 'send_at',
                            sortable: true,
                            title: t('launched_at'),
                            cell: ({ item: { send_at, type } }) => {
                                return send_at != null
                                    ? formatDate(preferences, send_at, 'Pp')
                                    : type === 'trigger'
                                        ? t('api_triggered')
                                        : <>&#8211;</>
                            },
                        },
                        {
                            key: 'updated_at',
                            title: t('updated_at'),
                            sortable: true,
                        },
                        {
                            key: 'options',
                            title: t('options'),
                            align: 'end',
                            cell: ({ item }) => (
                                <Menu size="small">
                                    <MenuItem icon={<EditIcon />} onClick={async () => await handleEditCampaign(item.id)}>
                                        {t('edit')}
                                    </MenuItem>
                                    <MenuItem icon={<FolderMoveIcon />}onClick={async () => setCampaignToMove(item)}>
                                        {t('folder_move')}
                                    </MenuItem>
                                    <MenuItem icon={<DuplicateIcon />} onClick={async () => await handleDuplicateCampaign(item)}>
                                        {t('duplicate')}
                                    </MenuItem>
                                    <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchiveCampaign(item.id)}>
                                        {t('archive')}
                                    </MenuItem>
                                </Menu>
                            ),
                        },
                    ]}
                    onSelectRow={async ({ id }) => { await navigate(id.toString()) }}
                    enableSearch
                    emptyMessage={emptyMessage}
                    filters={[
                        <MultiSelect
                            key="type"
                            size="small"
                            options={campaignTypes(t)}
                            value={state.params.filter?.type}
                            onChange={value => state.setParams({
                                ...state.params,
                                filter: {
                                    ...state.params.filter,
                                    type: value.length ? value : undefined,
                                },
                            })}
                            placeholder={t('type')}
                            toValue={v => v.key}
                        />,
                        <MultiSelect
                            key="channel"
                            size="small"
                            options={campaignChannels(t)}
                            value={state.params.filter?.channel}
                            onChange={value => state.setParams({
                                ...state.params,
                                filter: {
                                    ...state.params.filter,
                                    channel: value.length ? value : undefined,
                                },
                            })}
                            placeholder={t('channel')}
                            toValue={v => v.key}
                        />,
                        <MultiSelect
                            key="state"
                            size="small"
                            options={campaignStates(t)}
                            value={state.params.filter?.state}
                            onChange={value => state.setParams({
                                ...state.params,
                                filter: {
                                    ...state.params.filter,
                                    state: value.length ? value : undefined,
                                },
                            })}
                            placeholder={t('state')}
                            toValue={v => v.key}
                        />,
                        <TagPicker
                            key="tags"
                            entity="campaigns"
                            size="small"
                            value={state.params.filter?.tags ?? []}
                            onChange={value => state.setParams({
                                ...state.params,
                                filter: {
                                    ...state.params.filter,
                                    tags: value.length ? value : undefined,
                                },
                            })}
                            placeholder={t('tags')}
                        />,
                    ]}
                />
            </PageContent>
            <Modal
                open={isCreateOpen}
                onClose={setIsCreateOpen}
                title={t('create_campaign')}
                size="large"
            >
                <CampaignForm
                    onSave={handleCreateCampaign}
                    folderId={state.results?.folder.id } />
            </Modal>

            {!!campaignToMove && <FolderSelectModal
                type="campaign"
                open={!!campaignToMove}
                onClose={() => setCampaignToMove(undefined)}
                onSelect={handleMoveFolder}
            />}

            {campaignToDuplicate !== undefined && <DuplicateCampaignModal
                open={campaignToDuplicate !== undefined}
                onClose={() => setCampaignToDuplicate(undefined)}
                templates={campaignToDuplicate.templates}
                campaignType={campaignToDuplicate.type}
                onDuplicate={async options => await duplicateCampaign(campaignToDuplicate.id, options)}
            />}
        </>
    )
}
